import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useWishlist } from '../../context/WishlistContext';
import { useOrders } from '../../context/OrderContext';
import { products } from '../../data/products';

const UserProfile = ({ isOpen, onClose }) => {
  const { user, updateProfile, logout } = useAuth();
  const { wishlistItems } = useWishlist();
  const { orders } = useOrders();
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPhoto, setIsChangingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    avatar: user?.avatar || ''
  });

  // Orders are loaded automatically by OrderContext
  // No need for manual loading here

  if (!user || !isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    updateProfile(formData);
    setIsEditing(false);
    setIsChangingPhoto(false);
  };

  const handleCancel = () => {
    setFormData({
      name: user.name,
      email: user.email,
      avatar: user.avatar
    });
    setIsEditing(false);
    setIsChangingPhoto(false);
  };

  const handleAvatarChange = (newAvatar) => {
    setFormData(prev => ({ ...prev, avatar: newAvatar }));
    setIsChangingPhoto(false);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Image = e.target.result;
        setFormData(prev => ({ ...prev, avatar: base64Image }));
        setIsChangingPhoto(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const avatarOptions = [
    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1494790108755-2616b332c1c2?w=150&h=150&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&h=150&fit=crop&crop=face',
    'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl sm:rounded-2xl w-full max-w-sm sm:max-w-md lg:max-w-lg xl:max-w-xl p-4 sm:p-6 lg:p-8 transform transition-all duration-300 scale-100 max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Profile</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors touch-optimized"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Profile Avatar */}
        <div className="text-center mb-4 sm:mb-6">
          {formData.avatar || user.avatar ? (
            <img
              src={formData.avatar || user.avatar}
              alt={user.name}
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-full mx-auto mb-3 sm:mb-4 border-4 border-blue-100 object-cover"
            />
          ) : (
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full mx-auto mb-3 sm:mb-4 border-4 border-blue-100 bg-gray-200 flex items-center justify-center">
              <svg className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          )}
          <button 
            onClick={() => setIsChangingPhoto(!isChangingPhoto)}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            Change Photo
          </button>
          
          {/* Avatar Selection */}
          {isChangingPhoto && (
            <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">Choose a new profile picture:</p>
              
              {/* Upload from device */}
              <div className="mb-3 sm:mb-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full py-2 px-3 sm:px-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors text-sm touch-optimized"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Upload from Device
                </button>
              </div>

              {/* Preset avatars */}
              <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">Or choose from presets:</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
                {avatarOptions.map((avatar, index) => (
                  <button
                    key={index}
                    onClick={() => handleAvatarChange(avatar)}
                    className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full border-2 transition-all touch-optimized ${
                      formData.avatar === avatar
                        ? 'border-blue-500 ring-2 ring-blue-200'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={avatar}
                      alt={`Avatar option ${index + 1}`}
                      className="w-full h-full rounded-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Profile Info */}
        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name
            </label>
            {isEditing ? (
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            ) : (
              <p className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900">{user.name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            {isEditing ? (
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            ) : (
              <p className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900">{user.email}</p>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 pt-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{user.totalOrders || orders.length}</div>
              <div className="text-sm text-gray-600">Total Orders</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{wishlistItems.length}</div>
              <div className="text-sm text-gray-600">Wishlist Items</div>
            </div>
          </div>

          {/* Wishlist */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">My Wishlist ({wishlistItems.length})</h3>
            <div className="space-y-3">
              {wishlistItems.length > 0 ? (
                wishlistItems.slice(0, 5).map((item) => {
                  const product = products.find(p => p.id === item.id);
                  return product ? (
                    <div key={item.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <img 
                        src={product.image} 
                        alt={product.name} 
                        className="w-12 h-12 object-cover rounded"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 text-sm">{product.name}</p>
                        <p className="text-sm text-gray-600">${product.price}</p>
                      </div>
                    </div>
                  ) : null;
                })
              ) : (
                <p className="text-gray-600">No items in wishlist</p>
              )}
              {wishlistItems.length > 5 && (
                <p className="text-sm text-blue-600">+{wishlistItems.length - 5} more items</p>
              )}
            </div>
          </div>

          {/* Recent Orders */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Orders ({orders.length})</h3>
            <div className="space-y-3">
              {orders.length > 0 ? (
                orders.slice(0, 3).map((order, index) => (
                  <div key={order.id} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                            #{orders.length - index}
                          </span>
                          <p className="font-medium text-gray-900">Order {order.orderNumber || order.id}</p>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{new Date(order.date).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-gray-900">${order.total.toFixed(2)}</p>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                          order.status === 'Delivered' ? 'bg-green-100 text-green-800' :
                          order.status === 'Shipped' ? 'bg-blue-100 text-blue-800' :
                          order.status === 'Processing' ? 'bg-yellow-100 text-yellow-800' :
                          order.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {order.status === 'confirmed' ? 'Confirmed' : order.status}
                        </span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      {order.items.length} item{order.items.length > 1 ? 's' : ''} • 
                      {order.items.slice(0, 2).map((item, idx) => {
                        const product = products.find(p => p.id === item.id);
                        return product ? (
                          <span key={item.id}>
                            {idx > 0 ? ', ' : ' '}{product.name}
                          </span>
                        ) : null;
                      })}
                      {order.items.length > 2 && <span> +{order.items.length - 2} more</span>}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-600">No recent orders</p>
              )}
            </div>
          </div>

          {/* Order Tracking */}
          {orders.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Tracking</h3>
              <div className="space-y-4">
                {orders.slice(0, 2).map((order) => (
                  <div key={order.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">Order #{order.id}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        order.status === 'Delivered' ? 'bg-green-100 text-green-800' :
                        order.status === 'Shipped' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'Processing' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {order.status}
                      </span>
                    </div>
                    
                    {/* Order Progress */}
                    <div className="flex items-center space-x-4 mb-3">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full ${
                          order.tracking.ordered ? 'bg-green-500' : 'bg-gray-300'
                        }`}></div>
                        <span className="ml-2 text-xs text-gray-600">Ordered</span>
                      </div>
                      <div className={`flex-1 h-0.5 ${
                        order.tracking.processing ? 'bg-green-500' : 'bg-gray-300'
                      }`}></div>
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full ${
                          order.tracking.processing ? 'bg-green-500' : 'bg-gray-300'
                        }`}></div>
                        <span className="ml-2 text-xs text-gray-600">Processing</span>
                      </div>
                      <div className={`flex-1 h-0.5 ${
                        order.tracking.shipped ? 'bg-green-500' : 'bg-gray-300'
                      }`}></div>
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full ${
                          order.tracking.shipped ? 'bg-green-500' : 'bg-gray-300'
                        }`}></div>
                        <span className="ml-2 text-xs text-gray-600">Shipped</span>
                      </div>
                      <div className={`flex-1 h-0.5 ${
                        order.tracking.delivered ? 'bg-green-500' : 'bg-gray-300'
                      }`}></div>
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full ${
                          order.tracking.delivered ? 'bg-green-500' : 'bg-gray-300'
                        }`}></div>
                        <span className="ml-2 text-xs text-gray-600">Delivered</span>
                      </div>
                    </div>
                    
                    {/* Order Items Preview */}
                    <div className="text-sm text-gray-600">
                      <p>{order.items.length} item{order.items.length > 1 ? 's' : ''} • Total: ${order.total.toFixed(2)}</p>
                      {order.items.slice(0, 2).map((item, index) => {
                        const product = products.find(p => p.id === item.id);
                        return product ? (
                          <span key={item.id}>
                            {product.name} (x{item.quantity})
                            {index < Math.min(order.items.length, 2) - 1 ? ', ' : ''}
                          </span>
                        ) : null;
                      })}
                      {order.items.length > 2 && (
                        <span> and {order.items.length - 2} more...</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-6 space-y-3">
          {isEditing ? (
            <div className="flex gap-3">
              <button
                onClick={handleSave}
                className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Save Changes
              </button>
              <button
                onClick={handleCancel}
                className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="w-full border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
            >
              Edit Profile
            </button>
          )}

          <button
            onClick={() => {
              logout();
              onClose();
            }}
            className="w-full bg-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-700 transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
